import type { PersonData } from "../../../common/types.ts";
import type {
	CustomVoice,
	DraftCustomPersona,
	SelectedPersona,
} from "./customPersonaTypes.ts";

export type DraftValidationResult =
	| { ok: true }
	| {
			ok: false;
			errors: {
				name?: string;
				label?: string;
				personality?: string;
				voice?: string;
			};
	  };

export function validateDraft(
	draft: DraftCustomPersona,
): DraftValidationResult {
	const errors: {
		name?: string;
		label?: string;
		personality?: string;
		voice?: string;
	} = {};
	if (!draft.name.trim()) errors.name = "required";
	if (!draft.label.trim()) errors.label = "required";
	if (!draft.personality.trim()) errors.personality = "required";
	if (!validateVoice(draft.voice)) errors.voice = "required";
	if (Object.keys(errors).length === 0) return { ok: true };
	return { ok: false, errors };
}

function validateVoice(voice: CustomVoice): boolean {
	if (voice.kind === "preset") return voice.voiceId.trim().length > 0;
	return voice.sampleBase64.length > 0;
}

/**
 * Builds a `PersonData` payload from a selected persona.
 *
 * For preset personas, sets `personaId` so the server can resolve the
 * persona's image for D-ID. For custom personas, sets `speaker = "You"`
 * when the user supplied a voice sample (which triggers voice cloning
 * server-side), otherwise sets `speaker` to the chosen preset voice id.
 */
export function buildPersonData(
	selected: SelectedPersona,
	uploaded: { imageFile?: string; audioFile?: string },
): PersonData {
	if (selected.kind === "preset") {
		return {
			name: selected.persona.name,
			personDescription: selected.persona.personality,
			imageDescription: "",
			speaker: selected.persona.voice,
			personaId: selected.persona.id,
		};
	}
	const { draft } = selected;
	const base: PersonData = {
		name: draft.name,
		personDescription: draft.personality,
		imageDescription: "",
		speaker: draft.voice.kind === "clone" ? "You" : draft.voice.voiceId,
	};
	if (uploaded.imageFile) base.image = uploaded.imageFile;
	if (uploaded.audioFile) base.audio = uploaded.audioFile;
	return base;
}
